// src/components/product/AvisProduit.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

function AvisProduit({ productId }) {
  const { t, i18n } = useTranslation();

  const [avis, setAvis]                 = useState([]);
  const [loading, setLoading]           = useState(true);
  const [note, setNote]                 = useState(0);
  const [hoverNote, setHoverNote]       = useState(0);
  const [commentaire, setCommentaire]   = useState('');
  const [submitting, setSubmitting]     = useState(false);
  const [showForm, setShowForm]         = useState(false);
  const [filter, setFilter]             = useState('all');

  const fetchAvis = async () => {
    try {
      const res = await axios.get(`https://madadline.com/api/products/${productId}/avis`);
      const data = Array.isArray(res.data) ? res.data : [];
      setAvis(data);
    } catch (err) {
      console.error('Erreur chargement avis:', err);
      setAvis([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productId) {
      setLoading(true);
      fetchAvis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('auth_token');

    if (!token) { alert(t('reviews.alertLogin')); return; }
    if (note === 0) { alert(t('reviews.alertNote')); return; }
    if (!commentaire.trim()) { alert(t('reviews.alertComment')); return; }

    setSubmitting(true);
    try {
      await axios.post(
        'https://madadline.com/api/avis',
        { product_id: productId, note, commentaire },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCommentaire('');
      setNote(0);
      setShowForm(false);
      fetchAvis();
    } catch (err) {
      console.error('Erreur ajout avis:', err);
      alert(t('reviews.alertError'));
    } finally {
      setSubmitting(false);
    }
  };

  const total   = avis.length;
  const moyenne = total > 0
    ? (avis.reduce((sum, a) => sum + Number(a.note || 0), 0) / total).toFixed(1)
    : 0;

  const stars = [5, 4, 3, 2, 1].map(n => ({
    n,
    count: avis.filter(a => Number(a.note) === n).length,
    percent: total > 0
      ? Math.round((avis.filter(a => Number(a.note) === n).length / total) * 100)
      : 0,
  }));

  const filteredAvis = avis.filter(a => {
    if (filter === 'photo') return !!a.image;
    if (filter === 'recent') {
      const d = new Date(a.created_at || 0);
      const days30 = 30 * 24 * 60 * 60 * 1000;
      return Date.now() - d.getTime() < days30;
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const locale = i18n.language === 'ar' ? 'ar-MA' : i18n.language === 'en' ? 'en-GB' : 'fr-FR';
    try {
      return new Date(dateStr).toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return new Date(dateStr).toLocaleDateString();
    }
  };

  return (
    <section className="reviews" id="avis">

      <header className="reviews__head">
        <span className="reviews__label">{t('reviews.label')}</span>
        <h2 className="reviews__title">{t('reviews.title')}</h2>
      </header>

      {total > 0 && (
        <div className="reviews__summary">
          <div className="reviews__avg">
            <div className="reviews__avg-number">{moyenne}</div>
            <div className="reviews__avg-stars">
              {'★'.repeat(Math.round(moyenne))}
              <span className="reviews__avg-stars-empty">
                {'★'.repeat(5 - Math.round(moyenne))}
              </span>
            </div>
            <div className="reviews__avg-count">
              {t('reviews.avgCount', {
                n: total,
                plural: total > 1 ? 's' : '',
              })}
            </div>
          </div>

          <div className="reviews__breakdown">
            {stars.map(s => (
              <div key={s.n} className="reviews__breakdown-row">
                <span className="reviews__breakdown-label">{s.n} ★</span>
                <div className="reviews__breakdown-bar">
                  <div className="reviews__breakdown-fill" style={{ width: `${s.percent}%` }} />
                </div>
                <span className="reviews__breakdown-count">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="reviews__action">
        {!showForm ? (
          <button className="reviews__write-btn" onClick={() => setShowForm(true)}>
            {t('reviews.writeBtn')}
          </button>
        ) : (
          <button
            className="reviews__write-btn reviews__write-btn--cancel"
            onClick={() => setShowForm(false)}
          >
            {t('reviews.cancelBtn')}
          </button>
        )}
      </div>

      {showForm && (
        <form className="reviews__form" onSubmit={handleSubmit}>
          <div className="reviews__form-field">
            <label>{t('reviews.formNoteLabel')}</label>
            <div className="reviews__form-stars">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  type="button"
                  className={`reviews__form-star ${n <= (hoverNote || note) ? 'is-active' : ''}`}
                  onMouseEnter={() => setHoverNote(n)}
                  onMouseLeave={() => setHoverNote(0)}
                  onClick={() => setNote(n)}
                  aria-label={t('reviews.ariaStars', { n })}
                >
                  ★
                </button>
              ))}
              {note > 0 && (
                <span className="reviews__form-note">
                  {t('reviews.noteOf5', { note })}
                </span>
              )}
            </div>
          </div>

          <div className="reviews__form-field">
            <label htmlFor="review-comment">{t('reviews.formCommentLabel')}</label>
            <textarea
              id="review-comment"
              rows="4"
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              placeholder={t('reviews.formCommentPlaceholder')}
              required
            />
          </div>

          <button type="submit" className="reviews__submit" disabled={submitting}>
            {submitting ? t('reviews.formSubmitting') : t('reviews.formSubmit')}
          </button>

          <p className="reviews__legal">{t('reviews.legal')}</p>
        </form>
      )}

      {total > 0 && (
        <div className="reviews__filters">
          <button
            className={`reviews__filter ${filter === 'all' ? 'is-active' : ''}`}
            onClick={() => setFilter('all')}
          >
            {t('reviews.filterAll', { n: total })}
          </button>
          <button
            className={`reviews__filter ${filter === 'photo' ? 'is-active' : ''}`}
            onClick={() => setFilter('photo')}
          >
            {t('reviews.filterPhoto')}
          </button>
          <button
            className={`reviews__filter ${filter === 'recent' ? 'is-active' : ''}`}
            onClick={() => setFilter('recent')}
          >
            {t('reviews.filterRecent')}
          </button>
        </div>
      )}

      {loading ? (
        <div className="reviews__loading"><div className="spinner" /></div>
      ) : filteredAvis.length === 0 ? (
        <div className="reviews__empty">
          <div className="reviews__empty-icon">💬</div>
          <p>{t('reviews.emptyTitle')}</p>
          <p className="reviews__empty-sub">{t('reviews.emptySub')}</p>
        </div>
      ) : (
        <ul className="reviews__list">
          {filteredAvis.map(a => (
            <li key={a.id} className="reviews__item">
              <div className="reviews__item-head">
                <div className="reviews__item-avatar">
                  {(a.user?.name || 'M').charAt(0).toUpperCase()}
                </div>
                <div className="reviews__item-meta">
                  <div className="reviews__item-name">
                    {a.user?.name || t('reviews.customerFallback')}
                    {a.verified && (
                      <span className="reviews__item-verified">{t('reviews.verified')}</span>
                    )}
                  </div>
                  <div className="reviews__item-date">{formatDate(a.created_at)}</div>
                </div>
                <div className="reviews__item-stars">
                  {'★'.repeat(Number(a.note || 0))}
                  <span className="reviews__item-stars-empty">
                    {'★'.repeat(5 - Number(a.note || 0))}
                  </span>
                </div>
              </div>

              <p className="reviews__item-text">
                {a.commentaire || a.comment || a.content}
              </p>

              {a.image && (
                <div className="reviews__item-image">
                  <img src={a.image} alt="Photo cliente" loading="lazy" />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

    </section>
  );
}

export default AvisProduit;